import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, User, Building2, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Input, Button, Label } from "../components/Primitives";
import { Logo, LangToggle } from "../components/AppShell";

export default function Register() {
  const { t } = useI18n();
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState("PERSON");
  const [form, setForm] = useState({ name: "", org: "", email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const finish = async () => {
    const e = {};
    if (!form.email.trim()) e.email = "Email required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.password) e.password = "Password required";
    else if (form.password.length < 6) e.password = "Min 6 characters";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    const { error, needsConfirmation } = await signUp({
      email: form.email.trim(),
      password: form.password,
      account_type: accountType,
      full_name: form.name || null,
      org_name: accountType === "ORGANIZATION" ? (form.org || null) : null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    if (needsConfirmation) {
      toast.success("Check your inbox", { description: "Confirm your email to finish creating your account." });
      navigate("/");
      return;
    }
    toast.success(t("register.finish"));
    navigate("/app/dashboard");
  };

  const OPTIONS = [
    { key: "PERSON", icon: User, title: t("register.types.person.t"), desc: t("register.types.person.d") },
    { key: "ORGANIZATION", icon: Building2, title: t("register.types.organization.t"), desc: t("register.types.organization.d") },
  ];

  return (
    <div className="min-h-screen bg-carbon-900 text-ink-900 flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 80% 20%, rgba(46,107,255,0.20), transparent 40%)"
      }} />
      <header className="relative z-10 flex items-center justify-between px-6 py-5">
        <Logo />
        <LangToggle />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-xl">
          <div className="mb-6 flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-electric-600">{t("register.step")} {step} {t("register.of")} 3</p>
            <div className="flex gap-1.5">
              {[1,2,3].map((n) => <div key={n} className={`h-1 w-8 rounded-full transition-colors ${n <= step ? "bg-electric-500" : "bg-carbon-600"}`} />)}
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("register.title")}</h1>
          <p className="text-ink-700 mt-2 text-sm mb-6">{t("register.subtitle")}</p>

          <div className="bg-carbon-800 border border-carbon-600 rounded-2xl p-6 space-y-5 card-soft">
            {step === 1 && (
              <>
                <div>
                  <Label>{t("register.role")}</Label>
                  <p className="text-[11px] text-ink-500 mb-3 leading-snug">{t("register.roleHelp")}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {OPTIONS.map(({ key, icon: Icon, title, desc }) => {
                    const active = accountType === key;
                    return (
                      <button key={key} onClick={() => setAccountType(key)} data-testid={`type-${key}`}
                        className={`text-left p-4 rounded-xl border transition ${
                          active ? "border-electric-500 bg-electric-500/10" : "border-carbon-600 hover:border-carbon-500 bg-carbon-800"
                        }`}>
                        <Icon className={`w-5 h-5 mb-2 ${active ? "text-electric-600" : "text-ink-700"}`} />
                        <div className="text-sm font-medium text-ink-900">{title}</div>
                        <div className="text-[11px] text-ink-500 mt-0.5 leading-snug">{desc}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <Label>{t("register.name")}</Label>
                  <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Marco Ferrari" data-testid="register-name" />
                </div>
                {accountType === "ORGANIZATION" && (
                  <div>
                    <Label>{t("register.company")}</Label>
                    <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Ferrari Composites S.r.l." data-testid="register-org" />
                  </div>
                )}
              </>
            )}

            {step === 3 && (
              <>
                <div>
                  <Label>{t("register.email")}</Label>
                  <Input autoFocus type="email" autoComplete="email" value={form.email}
                    onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: null }); }}
                    placeholder="you@example.com" data-testid="register-email" required
                    className={errors.email ? "!border-red-500/60" : ""}
                  />
                  {errors.email && <p className="text-[11px] text-red-400 mt-1" data-testid="err-register-email">{errors.email}</p>}
                </div>
                <div>
                  <Label>{t("register.password")}</Label>
                  <div className="relative">
                    <Input type={showPass ? "text" : "password"} autoComplete="new-password" value={form.password}
                      onChange={(e) => { setForm({ ...form, password: e.target.value }); if (errors.password) setErrors({ ...errors, password: null }); }}
                      placeholder="••••••••" data-testid="register-password" required
                      className={`pr-10 ${errors.password ? "!border-red-500/60" : ""}`}
                    />
                    <button type="button" onClick={() => setShowPass((v) => !v)} data-testid="register-toggle-password"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-800"
                      aria-label={showPass ? "Hide password" : "Show password"}
                    >
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-[11px] text-red-400 mt-1" data-testid="err-register-password">{errors.password}</p>}
                </div>
              </>
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={back} data-testid="register-back-btn" className={step === 1 ? "opacity-40 pointer-events-none" : ""}>
                <ArrowLeft className="w-4 h-4" /> {t("register.back")}
              </Button>
              {step < 3 ? (
                <Button onClick={next} data-testid="register-next-btn">{t("register.continue")} <ArrowRight className="w-4 h-4" /></Button>
              ) : (
                <Button onClick={finish} data-testid="register-finish-btn" disabled={busy}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("register.finish")} <Check className="w-4 h-4" /></>}
                </Button>
              )}
            </div>
          </div>

          <p className="text-center text-sm text-ink-500 mt-6">
            {t("register.have")}{" "}
            <button onClick={() => navigate("/")} className="text-electric-600 font-medium">{t("register.signIn")}</button>
          </p>
        </div>
      </main>
    </div>
  );
}
